/**
 * Database Adapter
 *
 * Provides framework-agnostic database utilities that work across
 * Prisma, custom database implementations, and different runtime environments.
 *
 * Mock data is gated behind shouldUseMockData() and only available in development.
 * In production, the adapter returns empty arrays until real backend is connected.
 */

import { getMockTransactions, getMockAssets, getMockWallets, shouldUseMockData } from "../mocks";

// Transaction types
export interface Transaction {
  id: string;
  amount: number;
  currency: string;
  type: "buy" | "sell" | "transfer" | "deposit" | "withdrawal";
  status: "pending" | "completed" | "failed" | "cancelled";
  timestamp: Date;
  fromAddress?: string;
  toAddress?: string;
  txHash?: string;
  blockNumber?: number;
  gasUsed?: number;
  gasPrice?: number;
  description?: string;
}

// Asset types
export interface Asset {
  id: string;
  symbol: string;
  name: string;
  balance: number;
  value: number; // USD value
  change24h: number; // 24h change percentage
  chain: string;
  contractAddress?: string;
  decimals: number;
  logoUrl?: string;
  /** Asset is disabled/coming soon - shown greyed out, excluded from totals */
  disabled?: boolean;
}

// Wallet types
export interface Wallet {
  id: string;
  address: string;
  chain: string;
  type: "ethereum" | "bitcoin" | "solana";
  assets: Asset[];
  totalValue: number;
}

export interface DatabaseAdapter {
  // Transaction operations
  getTransactions: (userId: string, limit?: number, offset?: number) => Promise<Transaction[]>;
  getTransaction: (id: string) => Promise<Transaction | null>;
  createTransaction: (transaction: Omit<Transaction, "id" | "timestamp">) => Promise<Transaction>;
  updateTransaction: (id: string, updates: Partial<Transaction>) => Promise<Transaction>;

  // Asset operations
  getAssets: (userId: string) => Promise<Asset[]>;
  getAsset: (id: string) => Promise<Asset | null>;
  updateAssetBalance: (id: string, balance: number) => Promise<Asset>;

  // Wallet operations
  getWallets: (userId: string) => Promise<Wallet[]>;
  getWallet: (id: string) => Promise<Wallet | null>;
  addWallet: (wallet: Omit<Wallet, "id">) => Promise<Wallet>;

  // Dev mode indicator
  isUsingMockData: () => boolean;
}

/**
 * Creates a database adapter
 * In development: returns mock data from centralized mock module
 * In production: returns empty arrays (ready for real backend)
 */
const createDatabaseAdapter = (): DatabaseAdapter => {
  // Initialize with mock data (will be empty in production)
  const transactions: Transaction[] = getMockTransactions();
  const assets: Asset[] = getMockAssets();
  const wallets: Wallet[] = getMockWallets();

  return {
    isUsingMockData: () => shouldUseMockData(),

    getTransactions: async (_userId: string, limit = 50) => {
      return transactions.slice(0, limit);
    },
    getTransaction: async (id: string) => {
      return transactions.find((tx) => tx.id === id) || null;
    },
    createTransaction: async (transaction) => {
      const newTransaction: Transaction = {
        ...transaction,
        id: Date.now().toString(),
        timestamp: new Date(),
      };
      transactions.unshift(newTransaction);
      return newTransaction;
    },
    updateTransaction: async (id: string, updates) => {
      const index = transactions.findIndex((tx) => tx.id === id);
      if (index === -1) throw new Error("Transaction not found");

      transactions[index] = { ...transactions[index], ...updates };
      return transactions[index];
    },
    getAssets: async (_userId: string) => {
      return assets;
    },
    getAsset: async (id: string) => {
      return assets.find((asset) => asset.id === id) || null;
    },
    updateAssetBalance: async (id: string, balance: number) => {
      const asset = assets.find((a) => a.id === id);
      if (!asset) throw new Error("Asset not found");

      asset.balance = balance;
      asset.value = balance * (asset.symbol === "ETH" ? 2000 : 1);
      return asset;
    },
    getWallets: async (_userId: string) => {
      return wallets;
    },
    getWallet: async (id: string) => {
      return wallets.find((wallet) => wallet.id === id) || null;
    },
    addWallet: async (wallet) => {
      const newWallet: Wallet = {
        ...wallet,
        id: Date.now().toString(),
      };
      wallets.push(newWallet);
      return newWallet;
    },
  };
};

/**
 * Creates a database adapter
 */
export const createDatabase = (): DatabaseAdapter => {
  return createDatabaseAdapter();
};

// Singleton instance for stable reference across renders
const databaseInstance = createDatabase();

/**
 * Hook-like interface for database (React-compatible)
 * Returns a stable reference to avoid useEffect dependency issues
 */
export const useDatabase = () => {
  return databaseInstance;
};
